import React, { useContext, useState } from "react";
import ReactModal from "react-modal";
import { browserVersion, isMobile } from "react-device-detect";
import { useHistory, useParams } from "react-router-dom";

import { SettingsContext } from "../../../contexts";
import { Collapsible } from "../../../components";
import { Button, Icon, IconButton } from "../../../components/ui";
import Quote from "../../../order/components/Quote";
import QuoteList from "../../../order/components/Quotes/QuoteList";
import QuoteWorkspace from "../../../order/components/QuoteWorkspace";
import QuoteRoster from "../../../order/components/QuoteWorkspace/QuoteRoster";
import { quoteWorkspaceEnabled } from "../../../order/hooks/quoteWorkspace";

export function PxQuotes({
  item,
  getSelectionForItem,
  isOrderFlow,
  openQuote,
  closeQuote,
  orderFlowQuoteId,
  orderFlowItemId,
  bare = false,
}) {
  const settings = useContext(SettingsContext);
  const { requestType, requestId, itemId, quoteId } = useParams();
  const history = useHistory();
  // Only used by V1's react-collapsible open prop. V2's
  // CustomCollapsible ignores this and renders bare children, and the
  // content is no longer gated by <Visible>, so V2 always shows the
  // quote list. Initial state preserves V1's "collapsed unless URL
  // has quoteId" behaviour.
  const [isExpanded, setIsExpanded] = useState(!!quoteId);
  const [refreshList, setRefreshList] = useState(0);
  const [type, setType] = useState("sell");
  const { selectedQuote, onQuoteSelected } = getSelectionForItem(item);
  // New setup: no pop-ups — the quote opens in place of the list (see QuotesView).
  const inlineQuote = quoteWorkspaceEnabled();

  const onCloseQuote = () => {
    if (isOrderFlow) {
      closeQuote();
    } else {
      // Check if we're on the requests page and need to scroll to the specific request
      const currentPath = history.location.pathname;
      if (currentPath.includes('/requests/orders')) {
        history.push(`/requests/orders?scrollTo=${item.id}&${settings.iframeQueryParams}`);
      } else {
        history.push(
          history.location.pathname
            .split("/")
            .slice(0, -1)
            .join("/") + `?${settings.iframeQueryParams}`
        );
      }
    }
  };

  const goToQuote = (id) => {
    if (isOrderFlow) {
      openQuote(id, item.id);
    } else {
      history.push(
        `/request/${requestType}/${requestId}/calculation/${item.id ||
          "co"}/${id}?${settings.iframeQueryParams}`
      );
    }
  };

  const onQuoteSaved = (quoteId) => {
    setRefreshList(refreshList + 1);
    if (quoteId.id) {
      goToQuote(quoteId.id);
    }
  };

  const isQuoteModalOpen = isOrderFlow
    ? orderFlowItemId == item.id && orderFlowQuoteId
    : (item.id ? itemId == item.id : itemId == "co") && !!quoteId;

  const openQuoteId = isQuoteModalOpen ? quoteId || orderFlowQuoteId : null;

  // New setup: every quote stays listed — tick a green one to choose it — with
  // the open quote's workspace under the list. No pop-up.
  const quoteContent = inlineQuote ? (
    <div className="px-calc-quotes-section qws-stack">
      <QuoteRoster
        item={item}
        type={type}
        refresh={refreshList}
        openId={openQuoteId}
        onOpen={(id) => (String(id) === String(openQuoteId) ? onCloseQuote() : goToQuote(id))}
        chosenId={selectedQuote?.id}
        onChoose={onQuoteSelected}
        liveCalculation
        onNew={() => goToQuote("new")}
      />
      {!!openQuoteId && (
        <QuoteWorkspace
          key={openQuoteId}
          id={openQuoteId}
          item={item}
          onQuoteSaved={onQuoteSaved}
          onBack={onCloseQuote}
          navigateOnSave={!isOrderFlow}
        />
      )}
    </div>
  ) : (
    <div className="px-calc-quotes-section">
      <div className="px-quotes-label">Select Quote</div>
      <div className="quote-list">
        <QuoteList
          item={item}
          refresh={refreshList}
          selectItem={(id) => goToQuote(id)}
          chooseQuote={onQuoteSelected}
          chosenQuote={selectedQuote}
          onlyApproved={false}
          liveCalculation
          type={type}
          setType={setType}
          headerAction={
            <Button
              variant="primary"
              size="sm"
              data-btn="quote-new"
              leadingIcon={<Icon name="Plus" size="sm" />}
              onClick={() => goToQuote("new")}
            >
              New quote
            </Button>
          }
        />
      </div>

      <ReactModal
        {...{
          isOpen: !!isQuoteModalOpen,
          onRequestClose: onCloseQuote,
          // px-live-calc-quote-modal/-overlay: distinguish this instance so both
          // the content AND its react-modal overlay wrapper can be pushed above
          // `.px-live-calculation-page` (see px-live-calculation.scss) without
          // touching other `.product-preview-modal`/`.ReactModal__Overlay` usages
          // elsewhere. The overlay override is the one that actually matters —
          // style.scss's global `.ReactModal__Overlay { z-index: 1500 !important }`
          // sits BELOW the 1600 page, and since that overlay div establishes its
          // own stacking context, no z-index on the content div nested inside it
          // can escape that (same trap already hit once for the New Supplier
          // wizard — see _users-dark-theme.scss).
          className: `product-preview-modal px-live-calc-quote-modal ${
            browserVersion < 15 && !isMobile
              ? "old-mac-product-preview-modal"
              : ""
          }`,
          overlayClassName: "px-live-calc-quote-overlay",
        }}
      >
        <IconButton
          label="Close"
          className="quote-editor-close"
          onClick={onCloseQuote}
          data-btn="quote-editor-close"
        >
          <Icon name="X" size="sm" />
        </IconButton>

        <Quote
          id={quoteId || orderFlowQuoteId}
          item={item}
          onQuoteSaved={onQuoteSaved}
          navigateOnSave={!isOrderFlow}
          basePath={
            isOrderFlow
              ? "/users"
              : `/request/${requestType}/${requestId}/calculation/${item.id ||
                  "co"}/${quoteId}`
          }
        />
      </ReactModal>
    </div>
  );

  if (bare) {
    return quoteContent;
  }

  return (
    <Collapsible
      trigger="Select Quote"
      handleTriggerClick={() => setIsExpanded(!isExpanded)}
      open={isExpanded}
    >
      {quoteContent}
    </Collapsible>
  );
}
